import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Input from '../../../components/Input';
import { t } from 'i18next';
import { IModal } from 'customTypes';
import Portal from '../../../components/Portal';
import GroupChkModal from '../../../components/Modal/GroupChkModal';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../services/_common';
import { v4 } from 'uuid';
import { BtnBlue, BtnLightBlue, BtnRed } from '../../../components/Button';
import Textarea from '../../../components/Textarea';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { useSetAuth } from '../../../utils/useSetAuth';
import DatePickerComponent from '../../../components/DatePicker';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import { toast } from 'react-toastify';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import type { IExellentUser } from './excellentEmployee';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { logPost } from '../../../services/log';

interface IForm {
  rowState: any | null;
  setRowState: Dispatch<SetStateAction<any | null>>;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  setTableState: Dispatch<SetStateAction<IExellentUser[] | null>>;
  refetch: () => void;
}

interface IUser {
  key: string;
  name: string;
}

interface IUserPart {
  grCd: string;
  grName: string;
  subList: IUser[];
}

const today = todayYYYYMMDD();
const Form = ({ rowState, setRowState, isNewAdd, setIsNewAdd, setTableState, refetch }: IForm) => {
  const userInfo = useRecoilValue(userState);
  const [openModal, setOpenModal] = useState<IModal | any>({ status: false, type: '', title: '' });
  const [userPartList, setUserPartList] = useState<IUserPart[]>([]);
  const [selectedExcellentUser, setSelectedExcellentUser] = useState<any[]>([]);
  const { data: userPartApi } = useQuery(['/user/company/part'], () => apiGet({ path: '/user/company/part', req: { hCd: userInfo?.hCd } }));
  const cContentRef = useRef<HTMLTextAreaElement>(null);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const [contents, setContents] = useState({ cContents: '' });
  const [cDate, setCdate] = useState(today);
  const { data: companyUser } = GetFetchStatus({ path: '/user/company', req: { hCd: userInfo.hCd }, useLoginUserInfo: false });

  useEffect(() => {
    if (rowState) {
      if (rowState.userId) {
        setSelectedExcellentUser([{ label: rowState.userName, value: rowState.userId, userId: rowState.userId, partName: rowState.partCdName, positionName: rowState.positionName }]);
      }
      if (rowState.cDate) setCdate(rowState.cDate?.replaceAll('-', ''));
      setContents({ cContents: rowState.cContents });
    }
  }, [rowState]);

  useEffect(() => {
    if (isNewAdd) {
      setSelectedExcellentUser([{ label: '', value: '', userId: '', partName: '', positionName: '' }]);
      setCdate(today);
      setContents({ cContents: '' });
      setIsSaveClicked(false);
    }
  }, [isNewAdd]);

  useEffect(() => {
    userPartApi &&
      setUserPartList(
        userPartApi?.data?.data?.map((el: any) => ({
          grCd: v4(),
          grName: el.partName,
          subList: el.users.map((el2: any) => ({ key: el2.userId, name: el2.userName })),
        }))
      );
  }, [userPartApi]);

  const onClickSelectUser = () => {
    if (rowState) {
      const newCompanyUser = companyUser.map((el: any) => ({ ...el, label: el.userName, value: el.userId }));
      setOpenModal({ status: true, type: 'chipType', findList: newCompanyUser });
    }
  };

  const onClickSave = async () => {
    setIsSaveClicked(true);
    const req: any = { ...(rowState.cSeq ? { cSeq: rowState.cSeq } : { userId: selectedExcellentUser[0].userId }), cCd: userInfo.hCd, cDate, cContents: contents.cContents };
    let res;

    if (!rowState.cSeq && !selectedExcellentUser[0].userId) {
      toast.warning('우수 사원을 선택하세요');
    } else if (req.cDate?.trim().length !== 8) {
      toast.warning(t('적용일자를 확인하세요'));
    } else if (req.cContents?.trim() === '') {
      toast.warning(t('내용을 입력하세요'));
    } else {
      try {
        if (!rowState.cSeq) res = await apiPost({ path: '/cexcellent', contentType: 'application/json', req });
        else res = await apiPatch({ path: '/cexcellent', contentType: 'application/json', req });

        const { data, statusCode, message } = res.data;
        if (statusCode === 200) {
          toast.success(t(message));
          setIsSaveClicked(false);
          refetch();

          if (isNewAdd) {
            setRowState(data[data.length - 1]);
            setIsNewAdd(false);
            await logPost({
              hCd: userInfo.hCd,
              sCd: userInfo.sCd,
              userId: userInfo.userId,
              menu: '우수사원',
              action: '우수사원 저장',
              etc: `우수사원 ID : ${req.userId}`,
            });
          }
        }
      } catch (error) {
        console.error(error);
        throw new Error('error');
      }
    }
  };

  const deleteAPI = async () => {
    try {
      const req = { cCd: userInfo.hCd, cSeq: rowState.cSeq };
      const res = await apiDelete({ path: '/cexcellent', contentType: 'application/json', req });
      const { statusCode, message } = res.data;
      if (statusCode === 200) {
        toast.success(t(message));
        setIsNewAdd(true);
        refetch();
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '사내 관리 > 우수 사원',
          action: `우수 사원 삭제`,
          etc: `우수 사원 ID : ${rowState.userId}`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickDelete = () => {
    setOpenModal((prev: any) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  if (!companyUser || !userPartApi)
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
      </LoadingModalBackground>
    );

  return (
    <>
      <Root>
        <div className='inputForm'>
          <div className='inputForm-group-1280'>
            <div className='inputForm-row labelInInput'>
              <label htmlFor='createDate' className='required flex-start'>
                {t('우수 사원명')}
              </label>
              <input disabled value={selectedExcellentUser[0]?.label ?? ''} style={isSaveClicked ? applyBorderStyle(selectedExcellentUser[0]?.label, 'red', 'userId') : undefined} />
              {isNewAdd && <BtnLightBlue onClick={onClickSelectUser}>{t('선택')}</BtnLightBlue>}
            </div>
            <div className='inputForm-row labelOutInput'>
              <label htmlFor='cDate' className='required'>
                {t('적용일자')}
              </label>
              <div className='datePickerWrapper'>
                <DatePickerComponent startDate={cDate} setDate={setCdate} popperPlacement='bottom' />
              </div>
            </div>
            <div className='inputForm-row labelInInput'>
              <label htmlFor='partName' className='flex-start'>
                {t('부서명')}
              </label>
              <input disabled value={selectedExcellentUser[0]?.partName ?? ''} />
            </div>
            <div className='inputForm-row labelInInput'>
              <label htmlFor='positionName' className='flex-start'>
                {t('직급')}
              </label>
              <input disabled value={selectedExcellentUser[0]?.positionName ?? ''} />
            </div>
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='code' className='required'>
              {t('내용')}
            </label>
            <div>
              <Textarea
                name='cContents'
                state={contents}
                set={setContents}
                inputRef={cContentRef}
                placeholder='2,000자 이하 입력'
                display='block'
                flexDirection='row'
                maxLength={2000}
                getBorderStyle={isSaveClicked ? applyBorderStyle(contents.cContents, 'red', 'cContents') : undefined}
              />
              <div className='flex-end'>
                <TextCounter currentCnt={contents.cContents.length} maxLength={2000} />
              </div>
            </div>
          </div>
          {!isNewAdd && (
            <div className='detailInfo-group'>
              <div className='flex-col detailInfo'>
                <label htmlFor='createDate'>{t('등록일자')}</label>
                <div>{rowState?.wDate}</div>
              </div>
              <div className='flex-col detailInfo'>
                <label htmlFor='createUser'>{t('등록자')}</label>
                <div>{rowState.writer}</div>
              </div>

              <div className='flex-col detailInfo'>
                <label htmlFor='updateDate'>{t('수정일자')}</label>
                <div>{rowState.eDate}</div>
              </div>
              <div className='flex-col detailInfo'>
                <label htmlFor='updateUser'>{t('수정자')}</label>
                <div>{rowState.editor}</div>
              </div>
            </div>
          )}
        </div>
        <ButtonsWrapper>
          {auth.deleteAuth && !isNewAdd && <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
        </ButtonsWrapper>
      </Root>
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'chipType' && (
          <GroupChkModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            type='group'
            selectGroup={selectedExcellentUser}
            setSelectGroup={setSelectedExcellentUser}
            title={t('사용자 선택')}
            comparisonList={userPartList}
            isMultiSelect={false}
          />
        )}
        {openModal && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </>
  );
};

export default Form;

const TextCounterStyle = styled.div`
  color: var(--filled-amber);
  background-color: var(--tonal-amber);
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
  font-size: 0.875rem;
`;

const TextCounter = ({ currentCnt, maxLength }: { currentCnt: number; maxLength: number }) => {
  return (
    <TextCounterStyle>
      <span>{currentCnt.toLocaleString()}</span> / <span>{maxLength.toLocaleString()}</span>
    </TextCounterStyle>
  );
};

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const Root = styled.div`
  padding: 0.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 57px);
  overflow: auto;
  justify-content: flex-start;

  ul > li {
    width: 100% !important;
    max-width: none !important;
  }

  .inputForm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem;
    flex-grow: 1;
    overflow: auto;

    input {
      background-color: var(--tonal) !important;
    }

    textarea {
      width: calc(100% - 1.8rem);
    }
    button {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    button.gray {
      font-weight: 500;
      color: var(--zinc-200);
    }
    label {
      width: 4.875rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
      white-space: break-spaces;
      line-height: 1.4;
    }
    .inputForm-group-1280 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1280px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1536px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536.withBtn {
      flex-direction: row;
      gap: 0.5rem;
      button {
        transition: none;
      }
      @media (min-width: 1536px) {
        gap: 1rem 2rem;
        button {
          margin-left: 0;
          width: fit-content;
        }
      }
    }
    .inputForm-group-1536.withBtn.inputColumnFour {
      input {
        padding: 0;
      }
    }
    .inputForm-row.labelInInput {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-grow: 1;
      > div {
        flex-grow: 1;
      }
    }
    .inputForm-row.labelOutInput,
    .inputForm-row.labelInInput > div > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      input {
        flex-shrink: 1;
        height: 2.5rem;
        font-size: 0.875rem;
        /* padding: 0; */
        padding: 0 0.5rem;
        &:disabled {
          background-color: var(--tonal);
          color: var(--text-disabled);
        }
      }
      > .viewOnly {
        height: 2.5rem;
        font-size: 0.875rem;
        background-color: rgba(0, 0, 0, 0.05);
        color: var(--text-primary);
        border-radius: 0.25rem;
        padding: 0 0.75rem;
        display: flex;
        align-items: center;
      }
      > div {
        flex-grow: 1;
        > div > div > ul {
          height: 2.5rem;
          li {
            display: flex;
            span {
              flex-grow: 1;
              width: 4rem;
            }
          }
        }
      }
    }
    .detailInfo-group {
      margin: 1rem 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      @media (min-width: 1536px) {
        grid-template-columns: repeat(3, 1fr);
      }
      .flex-col.detailInfo {
        flex-grow: 1;
        width: 100%;
        label {
          width: 100%;
        }
        div {
          height: 2.5rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  input {
    :disabled {
      background-color: #eeeeee;
    }
  }

  .col-span-img {
    grid-row: span 5;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 17.5rem;
    border-radius: 0.25rem;
    gap: 0.5rem;
    overflow: hidden;
    user-select: none;

    .logo-input-container {
      img {
        max-width: 20rem;
        max-height: 15rem;
        height: auto;
      }
      svg {
        color: var(--text-secondary);
      }
      flex-grow: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.5rem;
      background-color: var(--tonal);
      justify-content: space-around;
      align-items: center;
    }
    > div {
      display: flex;
      gap: 0.25rem;
    }
  }
  .userImageWrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-height: 12rem;
    max-width: 18rem;
    justify-content: flex-end;
    overflow: hidden;
    color: var(--text-quaternary);
    label {
      width: fit-content;
      margin: 1rem 0 0 0;
      > div {
        line-height: 2;
        padding: 0 1rem;
        border-radius: 5px;
      }
    }

    img {
      max-height: 9rem;
    }

    .smallBtn {
      cursor: pointer;
      color: #eaeaea;
      background-color: #8950fc;
      border: 1px solid #8950fc;
      &:hover {
        color: #eaeaea;
        background-color: rgba(137, 80, 252, 0.6);
      }
    }
  }
  @media screen and (max-width: 1024px) {
  }
  @media screen and (max-width: 767px) {
    width: 100%;
    height: fit-content;
    overflow: auto;
  }
`;
